import { Stack } from '@mui/material';
import { compact } from 'lodash';
import { useMemo, useState } from 'react';

import { File05Icon } from '@/components/icons/File05Icon';
import { AIOnboardingModalVariant } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AISuggestionReviewCard } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard';
import { groupSuggestionsByEntityOrTestamentaryEntityID } from '@/modules/aiSuggestions/aiSuggestions.utils';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useEditEntityTrowser } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import {
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { TestamentaryEntityModal } from '@/modules/entities/testamentaryEntities/TestamentaryEntityModal';
import {
  EstateOnboardingSuggestionGroup,
  isEstateOnboardingReviewCardSuggestion,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import { groupEstateOnboardingSuggestionsByGroup } from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import { sumHoldingsGroupCurrentValue } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { EstateSubtrustCard } from '@/modules/estate/EstateTrusts/SubtrustsContainer/EstateSubtrustCard/EstateSubtrustCard';
import { SubtrustGroups } from '@/modules/estate/EstateTrusts/SubtrustsContainer/SubtrustsContainer';
import { groupSubtrustsByCreatedFrom } from '@/modules/estate/EstateTrusts/SubtrustsContainer/SubtrustsContainer.utils';
import { TrustSection } from '@/modules/estate/EstateTrusts/TrustContainer/EstateTrustEntityCard/EstateTrustEntityCard';
import {
  TRUST_CONTAINER_VARIANT_COLORS,
  TRUST_CONTAINER_VARIANT_ICON,
  TRUST_CONTAINER_VARIANT_TITLE,
  TrustContainerVariant,
} from '@/modules/estate/EstateTrusts/TrustContainer/TrustContainer.types';
import { filterTrustsByVariant } from '@/modules/estate/EstateTrusts/TrustContainer/TrustContainer.utils';
import { EstatePage_TestamentaryEntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { pluralize } from '@/utils/formatting/strings';

import { useReviewSuggestionCategoryData } from './ReviewSuggestion.hooks';
import { ReviewSuggestionLoadingState } from './ReviewSuggestion.LoadingState';
import { useReviewSuggestionOnboardingModal } from './ReviewSuggestion.OnboardingModal';
import { ReviewSuggestionProvider } from './ReviewSuggestion.provider';
import {
  ReviewSuggestionEntityGroupHeader,
  ReviewSuggestionSectionHeader,
} from './ReviewSuggestion.SectionHeader';
import { getEstateReviewSuggestions } from './ReviewSuggestions.utils';

const TRUST_GROUPS: TrustContainerVariant[] = [
  'in-estate',
  'out-of-estate-family',
  'out-of-estate-charity',
];

export function TrustReviewSuggestions() {
  const editEntityTrowser = useEditEntityTrowser();
  const canEdit = useCanEditHouseholdData();
  const { defaultFirstToDieClientId, isTwoClientHousehold } =
    useHouseholdDetailsContext();
  const data = useReviewSuggestionCategoryData({
    entityKinds: [...TRUST_ENTITY_KINDS, ...NON_TRUST_CHARITABLE_ENTITY_KINDS],
    includeOnboardingSuggestions: true,
    includeTestamentaryEntities: true,
  });
  const suggestionsByEntityID = useMemo(
    () => groupSuggestionsByEntityOrTestamentaryEntityID(data.suggestions),
    [data.suggestions]
  );
  const estateReview = useMemo(
    () =>
      getEstateReviewSuggestions({
        entities: data.entities,
        testamentaryEntities: data.testamentaryEntities,
        primaryDeathOrderClientId: defaultFirstToDieClientId ?? undefined,
        suggestionsByEntityID,
      }),
    [
      data.entities,
      data.testamentaryEntities,
      defaultFirstToDieClientId,
      suggestionsByEntityID,
    ]
  );
  const newSuggestions = useMemo(
    () =>
      groupEstateOnboardingSuggestionsByGroup(
        data.onboardingSuggestions.filter(
          isEstateOnboardingReviewCardSuggestion
        ),
        compact(data.entities)
      )[EstateOnboardingSuggestionGroup.TrustEntities],
    [data.entities, data.onboardingSuggestions]
  );
  const onboardingModal = useReviewSuggestionOnboardingModal({
    suggestions: newSuggestions,
    variant: AIOnboardingModalVariant.TRUST_ENTITIES,
  });
  const [testamentaryEntityModal, setTestamentaryEntityModal] = useState<{
    entity: EstatePage_TestamentaryEntityFragment;
    section?: EditTestamentaryEntitySection;
  } | null>(null);
  if (data.loading && data.entities.length === 0)
    return <ReviewSuggestionLoadingState />;

  return (
    <ReviewSuggestionProvider {...data}>
      <Stack spacing={3}>
        {newSuggestions.length > 0 && (
          <Stack spacing={2}>
            <ReviewSuggestionSectionHeader
              title={pluralize({
                count: newSuggestions.length,
                singular: 'newly identified trust',
                plural: 'newly identified trusts',
              })}
              description="The following trusts were identified in client documents, but have not been created yet."
            />
            <AISuggestionReviewCard
              variant={AIOnboardingModalVariant.TRUST_ENTITIES}
              displayMode="expanded-list"
              suggestionDisplayText={{ singular: 'trust', plural: 'trusts' }}
              suggestions={newSuggestions}
              onOpenAIOnboardingModal={onboardingModal.open}
              onSuggestionCreated={data.refetch}
              showProcessingBanner={false}
            />
          </Stack>
        )}
        {estateReview.trusts.length + estateReview.subtrusts.length > 0 && (
          <Stack spacing={2}>
            <ReviewSuggestionSectionHeader
              title={pluralize({
                count: estateReview.counts.trusts,
                singular: 'suggestion for an existing trust',
                plural: 'suggestions for existing trusts',
              })}
              description="The following existing trusts have outstanding suggestions."
            />
            {TRUST_GROUPS.map((variant) => {
              const trusts = filterTrustsByVariant(
                estateReview.trusts,
                variant
              );
              if (trusts.length === 0) return null;

              const colors = TRUST_CONTAINER_VARIANT_COLORS[variant];
              return (
                <Stack key={variant} spacing={1}>
                  <ReviewSuggestionEntityGroupHeader
                    title={TRUST_CONTAINER_VARIANT_TITLE[variant]}
                    subtitle={formatCurrencyNoDecimals(
                      sumHoldingsGroupCurrentValue(trusts)
                    )}
                    icon={TRUST_CONTAINER_VARIANT_ICON[variant]}
                    iconColor={colors.iconColor}
                    backgroundColor={colors.backgroundColor}
                  />
                  {trusts.map((trust) => (
                    <TrustSection
                      key={trust.id}
                      trust={trust}
                      onEditEntitySection={(entityId, section) =>
                        editEntityTrowser.openForEntity(entityId, { section })
                      }
                    />
                  ))}
                </Stack>
              );
            })}
            {estateReview.subtrusts.length > 0 && (
              <Stack spacing={1}>
                <ReviewSuggestionEntityGroupHeader
                  title="Sub-trusts"
                  icon={File05Icon}
                  iconColor={COLORS.TEXT.SECONDARY}
                  backgroundColor={COLORS.GRAY[100]}
                />
                <SubtrustGroups
                  {...groupSubtrustsByCreatedFrom(estateReview.subtrusts)}
                  renderSubtrusts={(subtrusts) => (
                    <Stack spacing={1}>
                      {subtrusts.map((subtrust) => (
                        <EstateSubtrustCard
                          key={subtrust.id}
                          subtrust={subtrust}
                          entitySuggestionsV2={
                            suggestionsByEntityID[subtrust.id]
                          }
                          canEdit={canEdit}
                          onAISuggestionsClick={(entity, section) =>
                            setTestamentaryEntityModal({ entity, section })
                          }
                          onViewSubtrustDetails={(entity) =>
                            setTestamentaryEntityModal({ entity })
                          }
                        />
                      ))}
                    </Stack>
                  )}
                />
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      {onboardingModal.modal}
      {testamentaryEntityModal && (
        <TestamentaryEntityModal
          isOpen
          onClose={() => setTestamentaryEntityModal(null)}
          testamentaryEntityId={testamentaryEntityModal.entity.id}
          deadClientIds={[]}
          isTwoClientHousehold={isTwoClientHousehold}
          initialSection={testamentaryEntityModal.section}
        />
      )}
    </ReviewSuggestionProvider>
  );
}
